import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

/**
 * Yadid Advisory branded registration screen. Same letterhead visual language
 * as Login.jsx — full-bleed navy panel + cream form panel + ochre hairlines.
 */

const NAVY = "#0F1E3D";
const OCHRE = "#B08048";
const CREAM = "#F5F5F4";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "Partner" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]" style={{ background: CREAM }} data-testid="register-page">
      {/* ─── Letterhead panel ────────────────────────────────────────── */}
      <div className="hidden lg:flex relative overflow-hidden flex-col justify-between p-14 xl:p-20" style={{ background: NAVY, color: CREAM }}>
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "3px 3px",
        }} />
        <div className="pointer-events-none absolute top-10 left-10 w-24 h-px" style={{ background: OCHRE, opacity: 0.7 }} />
        <div className="pointer-events-none absolute top-10 left-10 w-px h-24" style={{ background: OCHRE, opacity: 0.7 }} />
        <div className="pointer-events-none absolute bottom-10 right-10 w-24 h-px" style={{ background: OCHRE, opacity: 0.7 }} />
        <div className="pointer-events-none absolute bottom-10 right-10 w-px h-24" style={{ background: OCHRE, opacity: 0.7 }} />

        <div className="relative z-10 text-[0.625rem] tracking-[0.28em] uppercase font-mono" style={{ color: OCHRE }}>
          Yadid Advisory · Est. 2026
        </div>

        <div className="relative z-10">
          <div className="flex items-baseline gap-4 select-none">
            <span style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "clamp(3.5rem, 8vw, 6rem)", fontWeight: 700, letterSpacing: "0.02em", color: CREAM, lineHeight: 1 }}>
              YADID
            </span>
          </div>
          <div className="mt-3 pl-1 flex items-center gap-4">
            <div className="h-px w-16" style={{ background: OCHRE }} />
            <span style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontSize: "clamp(1rem, 1.6vw, 1.375rem)", letterSpacing: "0.4em", color: OCHRE, textTransform: "uppercase" }}>
              Advisory
            </span>
          </div>

          <p className="mt-14 max-w-lg font-serif text-lg xl:text-xl leading-relaxed" style={{ color: "rgba(245,245,244,0.86)", fontFamily: "Georgia, 'Times New Roman', serif" }}>
            Request access to the firm workspace.
            <span className="block mt-4 text-base xl:text-lg italic" style={{ color: "rgba(245,245,244,0.62)" }}>
              Every seat is provisioned by the partner. Fill in your details and we'll route the request through the firm's onboarding rhythm.
            </span>
          </p>
        </div>

        <div className="relative z-10 flex items-end justify-between text-[0.625rem] tracking-[0.28em] uppercase font-mono">
          <div style={{ color: "rgba(245,245,244,0.55)" }}>Confidential · firm-privileged</div>
          <div style={{ color: OCHRE }}>YadidOS</div>
        </div>
      </div>

      {/* ─── Form panel ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-center p-6 sm:p-12" style={{ background: CREAM }}>
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <div className="lg:hidden text-center mb-10">
            <div style={{ fontFamily: "Georgia, serif", fontWeight: 700, fontSize: "2.5rem", color: NAVY, letterSpacing: "0.02em", lineHeight: 1 }}>YADID</div>
            <div className="mt-2 flex items-center justify-center gap-3">
              <div className="h-px w-8" style={{ background: OCHRE }} />
              <span style={{ fontFamily: "Georgia, serif", fontSize: "0.75rem", letterSpacing: "0.4em", color: OCHRE, textTransform: "uppercase" }}>Advisory</span>
              <div className="h-px w-8" style={{ background: OCHRE }} />
            </div>
          </div>

          <div className="text-[0.625rem] font-mono uppercase tracking-[0.28em]" style={{ color: OCHRE }}>Firm access</div>
          <h2 className="mt-3" style={{ fontFamily: "Georgia, serif", fontSize: "2.25rem", fontWeight: 700, color: NAVY, letterSpacing: "-0.01em", lineHeight: 1.1 }}>
            Create account.
          </h2>
          <p className="mt-2 text-sm italic" style={{ color: "#6B7280", fontFamily: "Georgia, serif" }}>
            Set up your consultant profile.
          </p>

          <div className="mt-8 h-px" style={{ background: OCHRE, opacity: 0.6 }} />

          <div className="mt-8 space-y-5">
            <div>
              <Label className="text-[0.625rem] uppercase font-mono tracking-[0.24em]" style={{ color: NAVY, opacity: 0.7 }}>Full name</Label>
              <Input
                data-testid="register-name" required autoComplete="name"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-none mt-2 h-11 bg-white border-0 border-b-2 focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
                style={{ borderBottomColor: NAVY, color: NAVY, fontFamily: "Georgia, serif" }}
              />
            </div>
            <div>
              <Label className="text-[0.625rem] uppercase font-mono tracking-[0.24em]" style={{ color: NAVY, opacity: 0.7 }}>Email</Label>
              <Input
                data-testid="register-email" required type="email" autoComplete="email"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-none mt-2 h-11 bg-white border-0 border-b-2 focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
                style={{ borderBottomColor: NAVY, color: NAVY, fontFamily: "Georgia, serif" }}
              />
            </div>
            <div>
              <Label className="text-[0.625rem] uppercase font-mono tracking-[0.24em]" style={{ color: NAVY, opacity: 0.7 }}>Password</Label>
              <Input
                data-testid="register-password" required minLength={6} type="password" autoComplete="new-password"
                value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="rounded-none mt-2 h-11 bg-white border-0 border-b-2 focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
                style={{ borderBottomColor: NAVY, color: NAVY, fontFamily: "Georgia, serif" }}
              />
            </div>
            <div>
              <Label className="text-[0.625rem] uppercase font-mono tracking-[0.24em]" style={{ color: NAVY, opacity: 0.7 }}>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger
                  data-testid="register-role"
                  className="rounded-none mt-2 h-11 bg-white border-0 border-b-2 focus:ring-0"
                  style={{ borderBottomColor: NAVY, color: NAVY, fontFamily: "Georgia, serif" }}
                ><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Partner", "Principal", "Manager", "Senior Consultant", "Consultant", "Analyst"].map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Button
            data-testid="register-submit" disabled={busy}
            className="w-full rounded-none mt-8 h-12 border-0 hover:opacity-90 transition-opacity"
            style={{ background: NAVY, color: CREAM, letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: "Helvetica, Arial, sans-serif", fontSize: "0.7rem", fontWeight: 700 }}
          >
            {busy ? "Creating…" : "Create account"}
          </Button>

          <p className="text-xs mt-8 text-center" style={{ color: "#6B7280" }}>
            Already registered?{" "}
            <Link to="/login" className="hover:underline" style={{ color: NAVY, fontWeight: 600 }} data-testid="link-login">
              Sign in
            </Link>
          </p>

          <div className="mt-16 text-[0.5625rem] font-mono uppercase tracking-[0.28em] text-center" style={{ color: "#8A8A80" }}>
            Firm-privileged · Prepared by YadidAi
          </div>
        </form>
      </div>
    </div>
  );
}
